/**
 * Maquetación de cheques para el API renderizador genérico (POST /Print/render).
 *
 * IDEA: aquí vive TODO el diseño del cheque (posiciones X/Y, tamaños de fuente,
 * negritas y el monto en letras). El API solo dibuja lo que se le manda, así que
 * para mover algo o cambiar un tamaño se edita SOLO este archivo (deploy de
 * frontend), sin recompilar ni republicar el API de Windows.
 *
 * CALIBRACIÓN: cada banco tiene { offsetX, offsetY, lineFactor } guardado en
 * localStorage. Se ajusta desde la pantalla de prueba (GenerateCheck.vue) y lo
 * usa tanto la prueba como la impresión real, sin tocar código.
 *
 * Unidades: 1/100 de pulgada (igual que System.Drawing en modo impresora).
 * Fuente base: Courier New (monoespaciada) -> columnas alineadas con padStart/padEnd.
 */

import { NumberToLetter } from "convertir-nombre-lettre";

// Factor de alto de línea por defecto: GetHeight(pt) en 1/100" para Courier New
// = pt * (100/72) * 1.1641. Es el número a calibrar si las filas se corren vertical.
const DEFAULT_LINE_FACTOR = 1.6168;

// Facturas (líneas) que caben en el voucher del cheque. Si una transacción paga más,
// el resto va en el PDF "listado pendiente" (anexo) y el cheque muestra "VER ANEXO".
export const MAX_INVOICES_PER_CHECK = 18;

// pageHeight = alto físico del cheque en 1/100" (CRÍTICO en formulario continuo: cada
// página debe medir esto para que el 2º cheque no se corra). pageWidth 0 = usa el ancho
// por defecto de la impresora. Ajustables desde la pantalla de prueba.
const DEFAULTS = {
  popular: { offsetX: 0, offsetY: 0, dateOffsetX: 0, dateOffsetY: 0, lineFactor: DEFAULT_LINE_FACTOR, pageWidth: 0, pageHeight: 700 },
  banreservas: { offsetX: 0, offsetY: 0, dateOffsetX: 0, dateOffsetY: 0, lineFactor: DEFAULT_LINE_FACTOR, pageWidth: 0, pageHeight: 700 },
  codevi: { offsetX: 0, offsetY: 0, dateOffsetX: 0, dateOffsetY: 0, lineFactor: DEFAULT_LINE_FACTOR, pageWidth: 0, pageHeight: 700 },
};

const STORAGE_KEY = "check_calibration";

// Campos que se pueden ajustar por separado (ajuste en 1/100", guardado en la cuenta).
// En Popular/Banreservas la fecha usa dateOffsetX/Y (columnas propias en BD);
// el resto va en fieldOffsets = { campo: { x, y } }.
export const CHECK_FIELDS = {
  popular: [
    { key: "table", label: "Facturas" },
    { key: "date", label: "Fecha" },
    { key: "supplier", label: "Beneficiario" },
    { key: "amount", label: "Monto" },
    { key: "words", label: "Monto en letras" },
  ],
  codevi: [
    { key: "table", label: "Facturas" },
    { key: "header", label: "No. / fecha / suplidor" },
    { key: "amount", label: "Monto" },
    { key: "words", label: "Monto en letras" },
    { key: "supplier", label: "Beneficiario y dirección" },
  ],
};
CHECK_FIELDS.banreservas = CHECK_FIELDS.popular;

export const MAX_FIELD_OFFSET = 100;

// Deja solo campos conocidos de la plantilla con { x, y } numéricos; descarta ceros.
export function normalizeFieldOffsets(bankType, raw) {
  if (raw == null || raw === "") return {};
  if (typeof raw === "string") raw = JSON.parse(raw);
  if (typeof raw !== "object" || Array.isArray(raw)) throw new Error("Ajustes por campo no válidos.");
  const allowed = (CHECK_FIELDS[bankType] || []).map((f) => f.key).filter((k) => k !== "date" || bankType === "codevi");
  const out = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!allowed.includes(key)) continue;
    const x = Number(value && value.x) || 0;
    const y = Number(value && value.y) || 0;
    if (!Number.isFinite(x) || !Number.isFinite(y) || Math.abs(x) > MAX_FIELD_OFFSET || Math.abs(y) > MAX_FIELD_OFFSET) {
      throw new Error(`El ajuste de "${key}" debe estar entre -${MAX_FIELD_OFFSET} y ${MAX_FIELD_OFFSET} (1/100 de pulgada).`);
    }
    if (x || y) out[key] = { x, y };
  }
  return out;
}

export function getCalibration(type) {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return { ...DEFAULTS[type], fieldOffsets: {}, ...(raw[type] || {}) };
  } catch (e) {
    return { ...DEFAULTS[type], fieldOffsets: {} };
  }
}

export function saveCalibration(type, opts) {
  opts = validateCalibration(type, opts);
  let raw = {};
  try {
    raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch (e) {
    raw = {};
  }
  raw[type] = {
    offsetX: Number(opts.offsetX) || 0,
    offsetY: Number(opts.offsetY) || 0,
    dateOffsetX: opts.dateOffsetX,
    dateOffsetY: opts.dateOffsetY,
    lineFactor: Number(opts.lineFactor) || DEFAULT_LINE_FACTOR,
    pageWidth: Number(opts.pageWidth) || 0,
    pageHeight: Number(opts.pageHeight) || DEFAULTS[type].pageHeight,
    fieldOffsets: opts.fieldOffsets,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
  return raw[type];
}

// ---------------------------------------------------------------------------
// Constantes de maquetación por banco. Ajusta contra el cheque físico.
// ---------------------------------------------------------------------------
const POPULAR = {
  startY: 52, // tabla empieza 1 línea antes (antes 65); tableLines +1 mantiene fijo el bloque inferior
  xInvoices: 25,
  // RECIBO No. + FACTURA No. se COMBINAN en un solo campo para el No. de factura
  // (ej. FCLM00030000000031), así cabe a tamaño normal sin pisar fecha ni montos.
  xFecha: 192, // FECHA DE FACT.
  xMoney: 292, // MONTO FACTURA / PAGADO / DESCUENTO / NETO (siempre alineados)
  invoiceNoChars: 24, // ancho combinado RECIBO+FACTURA en caracteres (auto-encoge si excede)
  cellMinFont: 5, // tamaño mínimo al auto-encoger un No. de factura muy largo
  invoiceFont: 8,
  tableLines: 28, // +1 respecto a startY -13: el bloque inferior (fecha/nombre/letras) no se mueve
  maxInvoices: MAX_INVOICES_PER_CHECK,
  // Sube/baja EN BLOQUE la fecha, el beneficiario y el monto en letras (1/100").
  // Negativo = sube. Ajusta este número si esos 3 caen fuera de sus casillas.
  bodyOffsetY: -15,
  // Líneas en blanco extra ANTES del bloque inferior (fecha/beneficiario/letras).
  // Popular = 0. Sirve para bancos cuyo bloque inferior cae más abajo (ver Banreservas).
  bodyExtraLines: 0,
  xDate: 650, // Fecha 0.10" (2.54 mm) a la izquierda para el formulario Popular.
  dateOffsetY: 8,
  dateFont: 10,
  xSupplier: 165,
  supplierGap: 20, // separación mínima entre el final del nombre y el monto
  supplierMinFont: 7, // el nombre se achica hasta este tamaño antes de cortarse
  ySupplierOffset: -7,
  xAmount: 612,
  amountFont: 12,
  // Negrita del MONTO EN NÚMEROS (ej. 34,569.00). En Courier New negrita los dígitos
  // se empastan/borronean en la impresora -> false = se imprime normal (más legible).
  amountBold: false,
  xWords: 38,
  wordsFont: 12,
  wordsWidth: 72, // 1ª línea usa todo el ancho disponible (cabe "...PESOS CON 78/100" completo)
  wordsContIndent: 5, // si igual wrapea (montos grandes), la 2ª línea se corre 5 caracteres para no chocar con el logo POPULAR
};

// BANRESERVAS comparte el layout de Banco Popular (DOP · monto en letras en español),
// pero su bloque inferior (fecha día/mes/año, beneficiario y monto en letras) cae 2 líneas
// más abajo en el formulario. Solo cambia bodyExtraLines; el resto se hereda de POPULAR.
// Si la fecha/beneficiario/letras caen fuera de la casilla, ajusta bodyExtraLines o bodyOffsetY.
const BANRESERVAS = {
  ...POPULAR,
  xDate: 660,
  bodyExtraLines: 2,
  // Monto en NÚMEROS en negrita (ej. 34,569.00). Solo Banreservas; Popular lo deja normal.
  amountBold: true,
};

const CODEVI = {
  startY: 55,
  xInvoices: 6,
  // RECIBO No. + FACTURA No. combinados para el No. de factura (igual que Popular).
  xFecha: 172, // FECHA DE FACT.
  xMoney: 273, // MONTO FACTURA / PAGADO / DESCUENTO / NETO (siempre alineados)
  invoiceNoChars: 24, // ancho combinado RECIBO+FACTURA en caracteres (auto-encoge si excede)
  cellMinFont: 5, // tamaño mínimo al auto-encoger un No. de factura muy largo
  invoiceFont: 8,
  tableLines: 26,
  maxInvoices: MAX_INVOICES_PER_CHECK,
  xHeader: 22,
  headerFont: 10,
  xAmount: 612,
  amountFont: 12,
  blankAfterHeader: 7,
  xWords: 10,
  wordsFont: 12,
  wordsWidth: 51,
  gapAfterWords: 30,
  xSupplier: 85,
  supplierFont: 13,
  supplierMinFont: 8,
  maxRight: 800, // borde derecho útil del formulario (1/100")
  gapSupplierToAddress: 25,
};

// ---------------------------------------------------------------------------
// Conversor de monto a letras en ESPAÑOL: "SEIS MIL PESOS CON 00/100"
// ---------------------------------------------------------------------------
const UNIDADES = [
  "", "UNO", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE",
  "DIEZ", "ONCE", "DOCE", "TRECE", "CATORCE", "QUINCE", "DIECISÉIS", "DIECISIETE",
  "DIECIOCHO", "DIECINUEVE", "VEINTE", "VEINTIUNO", "VEINTIDÓS", "VEINTITRÉS",
  "VEINTICUATRO", "VEINTICINCO", "VEINTISÉIS", "VEINTISIETE", "VEINTIOCHO", "VEINTINUEVE",
];
const DECENAS = ["", "", "", "TREINTA", "CUARENTA", "CINCUENTA", "SESENTA", "SETENTA", "OCHENTA", "NOVENTA"];
const CENTENAS = [
  "", "CIENTO", "DOSCIENTOS", "TRESCIENTOS", "CUATROCIENTOS", "QUINIENTOS",
  "SEISCIENTOS", "SETECIENTOS", "OCHOCIENTOS", "NOVECIENTOS",
];

function groupEs(n, apocope) {
  if (n === 0) return "";
  if (n === 100) return "CIEN";
  let s = "";
  const c = Math.floor(n / 100);
  const r = n % 100;
  if (c > 0) s += CENTENAS[c] + " ";
  if (r > 0) {
    if (r < 30) {
      let w = UNIDADES[r];
      if (apocope && r === 1) w = "UN";
      else if (apocope && r === 21) w = "VEINTIÚN";
      s += w;
    } else {
      const d = Math.floor(r / 10);
      const u = r % 10;
      s += DECENAS[d];
      if (u > 0) {
        let w = UNIDADES[u];
        if (apocope && u === 1) w = "UN";
        s += " Y " + w;
      }
    }
  }
  return s.trim();
}

function belowMillionEs(n) {
  const miles = Math.floor(n / 1000);
  const r = n % 1000;
  let s = "";
  if (miles > 0) {
    s += miles === 1 ? "MIL" : groupEs(miles, true) + " MIL";
    if (r > 0) s += " ";
  }
  if (r > 0) s += groupEs(r, true);
  return s.trim();
}

function integerToWordsEs(n) {
  if (n === 0) return "CERO";
  const millones = Math.floor(n / 1000000);
  const r = n % 1000000;
  let s = "";
  if (millones > 0) {
    s += millones === 1 ? "UN MILLÓN" : belowMillionEs(millones) + " MILLONES";
    if (r > 0) s += " ";
  }
  if (r > 0) s += belowMillionEs(r);
  return s.trim();
}

export function montoEnLetras(amount, plural = "PESOS", singular = "PESO") {
  let value = Math.abs(Number(amount) || 0);
  let entero = Math.floor(value);
  let centavos = Math.round((value - entero) * 100);
  if (centavos === 100) {
    entero += 1;
    centavos = 0;
  }
  let letras = integerToWordsEs(entero);
  let unidad = entero === 1 ? singular : plural;
  if (entero >= 1000000 && entero % 1000000 === 0) unidad = "DE " + unidad;
  return `${letras} ${unidad} CON ${String(centavos).padStart(2, "0")}/100`;
}

// ---------------------------------------------------------------------------
// Conversor de monto a letras en INGLÉS: "SIX THOUSAND DOLLARS AND 00/100"
// ---------------------------------------------------------------------------
const ONES_EN = [
  "", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE", "TEN",
  "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN", "FIFTEEN", "SIXTEEN", "SEVENTEEN", "EIGHTEEN", "NINETEEN",
];
const TENS_EN = ["", "", "TWENTY", "THIRTY", "FORTY", "FIFTY", "SIXTY", "SEVENTY", "EIGHTY", "NINETY"];
const SCALES_EN = ["", "THOUSAND", "MILLION", "BILLION", "TRILLION"];

function below1000En(n) {
  let s = "";
  const h = Math.floor(n / 100);
  const r = n % 100;
  if (h > 0) {
    s += ONES_EN[h] + " HUNDRED";
    if (r > 0) s += " ";
  }
  if (r > 0) {
    if (r < 20) s += ONES_EN[r];
    else {
      s += TENS_EN[Math.floor(r / 10)];
      if (r % 10 > 0) s += "-" + ONES_EN[r % 10];
    }
  }
  return s;
}

export function amountToWordsEn(amount, plural = "DOLLARS", singular = "DOLLAR") {
  let value = Math.abs(Number(amount) || 0);
  let entero = Math.floor(value);
  let cents = Math.round((value - entero) * 100);
  if (cents === 100) {
    entero += 1;
    cents = 0;
  }
  let words;
  if (entero === 0) {
    words = "ZERO";
  } else {
    const groups = [];
    let n = entero;
    while (n > 0) {
      groups.push(n % 1000);
      n = Math.floor(n / 1000);
    }
    const parts = [];
    for (let i = groups.length - 1; i >= 0; i--) {
      if (groups[i] === 0) continue;
      parts.push(below1000En(groups[i]) + (SCALES_EN[i] ? " " + SCALES_EN[i] : ""));
    }
    words = parts.join(" ");
  }
  const unit = entero === 1 ? singular : plural;
  return `${words} ${unit} AND ${String(cents).padStart(2, "0")}/100`;
}

// ---------------------------------------------------------------------------
// Conversor de monto a letras en FRANCÉS para HTG (gourdes). Usa la librería
// `convertir-nombre-lettre`, igual que los volantes del sistema de RRHH, con
// el mismo formato: "VINGT-CINQ MILLE ... GOURDES AK 50/100".
// ---------------------------------------------------------------------------
export function montoEnLetrasFr(amount) {
  const value = Math.abs(Number(amount) || 0);
  const fixed = value.toFixed(2);
  const ent = fixed.split(".")[0];
  const dec = fixed.split(".")[1];
  const words = (NumberToLetter(ent) || "").toString().toUpperCase().replace(/\s+/g, " ").trim();
  return `${words} GOURDES AK ${dec}/100`;
}

// ---------------------------------------------------------------------------
// Helpers de formato y de wrapping
// ---------------------------------------------------------------------------
const money = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const padR = (v, len) => String(v ?? "").padEnd(len);
const padL = (v, len) => String(v ?? "").padStart(len);

// Devuelve una fuente más chica si el texto no cabe en `maxChars` (para que un
// valor largo, como un No. de recibo, quepa en su casilla sin pisar las demás).
function fitFont(text, maxChars, baseFont, minFont = 5) {
  const len = String(text ?? "").length;
  if (len <= maxChars || len === 0) return baseFont;
  return Math.max(minFont, +((baseFont * maxChars) / len).toFixed(2));
}

// Ancho de un carácter de Courier New en 1/100" para un tamaño en puntos.
const charWidth = (pt) => pt * 1.3889 * 0.6;

// Ajusta un texto a un ancho máximo (1/100"): primero achica la fuente hasta `minFont`
// y, solo si aun así no cabe, lo corta. `dy` baja el texto para que, más chico,
// quede sobre la misma línea base que a tamaño normal.
function fitText(text, maxWidth, baseFont, minFont) {
  const value = String(text ?? "").trim();
  const chars = (pt) => Math.floor(maxWidth / charWidth(pt));
  if (value.length <= chars(baseFont)) return { text: value, font: baseFont, dy: 0 };
  const font = Math.max(minFont, Math.floor(((maxWidth / value.length) / (1.3889 * 0.6)) * 10) / 10);
  const dy = (baseFont - font) * 1.3889 * 0.8;
  return { text: value.slice(0, chars(font)).trimEnd(), font, dy };
}

// Crea un "pusher" que aplica los offsets de calibración a cada elemento.
// `field` identifica el campo (fecha, beneficiario, monto…) y suma su ajuste guardado en la cuenta.
function makePusher(elements, opts) {
  const ox = Number(opts.offsetX) || 0;
  const oy = Number(opts.offsetY) || 0;
  const fo = opts.fieldOffsets || {};
  return (text, x, y, fontSize, bold = false, field = null) => {
    const f = (field && fo[field]) || { x: 0, y: 0 };
    elements.push({ Text: String(text ?? ""), X: x + ox + f.x, Y: y + oy + f.y, FontSize: fontSize, Bold: bold, Field: field });
  };
}

// Envuelve un texto largo en líneas y las empuja (avanzando y). Las líneas de
// continuación (2ª en adelante) se indentan `contIndentChars` caracteres para esquivar
// elementos preimpresos (ej. el logo del banco); su ancho útil se reduce lo mismo.
function pushWrapped(push, text, x, startY, fontSize, width, lineH, contIndentChars = 0, field = null) {
  const charW = fontSize * 1.3889 * 0.6; // ancho de carácter Courier New en 1/100"
  let y = startY;
  const words = String(text).split(" ");
  let line = "";
  let first = true;
  const curWidth = () => (first ? width : width - contIndentChars);
  const curX = () => (first ? x : x + contIndentChars * charW);
  words.forEach((w) => {
    if (line.length + w.length + 1 <= curWidth()) {
      line += w + " ";
    } else {
      push(line.trim(), curX(), y, fontSize, false, field);
      y += lineH;
      first = false;
      line = w + " ";
    }
  });
  push(line.trim(), curX(), y, fontSize, false, field);
  return y;
}

// ---------------------------------------------------------------------------
// BANCO POPULAR / BANRESERVAS (DOP) -> monto en letras en ESPAÑOL
// ---------------------------------------------------------------------------
// Builder genérico para cheques estilo Banco Popular (DOP · letras en español).
// `C` = constantes de maquetación (POPULAR | BANRESERVAS), `defaults` = calibración base.
function buildPopularStyleCheckPages(cheques, opts, C, defaults) {
  const o = { ...defaults, ...opts };
  // La fecha conserva sus columnas propias (dateOffsetX/Y) y se aplica como un campo más.
  o.fieldOffsets = { ...(o.fieldOffsets || {}), date: { x: Number(o.dateOffsetX) || 0, y: Number(o.dateOffsetY) || 0 } };
  const lh = (pt) => pt * o.lineFactor;

  return cheques.map((c) => {
    const elements = [];
    const push = makePusher(elements, o);
    let y = C.startY;

    const invoices = c.InvoiceDetails || [];
    const shown = invoices.slice(0, C.maxInvoices);

    shown.forEach((inv) => {
      // RECIBO No. + FACTURA No. combinados: SOLO el No. de factura (auto-encoge si excede).
      push(inv.VoucherNo, C.xInvoices, y, fitFont(inv.VoucherNo, C.invoiceNoChars, C.invoiceFont, C.cellMinFont), false, "table");
      // FECHA DE FACT.
      push(inv.invoiceDate, C.xFecha, y, C.invoiceFont, false, "table");
      // Montos en bloque, en X fija -> siempre alineados con sus casillas.
      const montos =
        `${padL(money(inv.invoiceAmount), 16)} ${padL(money(inv.amountPaid), 19)}  ` +
        `${padL(money(inv.discountTaken), 15)}  ${padL(money(inv.netCheckAmount), 15)}`;
      push(montos, C.xMoney, y, C.invoiceFont, false, "table");
      y += lh(C.invoiceFont);
    });

    if (invoices.length > C.maxInvoices) {
      const extra = invoices.length - C.maxInvoices;
      push(`VER ANEXO -> ${extra} FACTURA(S) ADICIONAL(ES) EN LISTADO PENDIENTE`, C.xInvoices, y, C.invoiceFont, true, "table");
      y += lh(C.invoiceFont);
    }

    const used = invoices.length > C.maxInvoices ? C.maxInvoices + 1 : invoices.length;
    for (let i = 0; i < C.tableLines - used; i++) y += lh(C.invoiceFont);

    // Ajuste fino del bloque inferior del cheque (fecha, beneficiario, monto en letras).
    y += C.bodyOffsetY;
    // Líneas en blanco extra antes de la fecha (Banreservas baja el bloque 2 líneas).
    y += (C.bodyExtraLines || 0) * lh(C.invoiceFont);
    const spacedDate = String(c.chequeDate || "").replace(/\//g, "").split("").join(" ");
    push(spacedDate + "  ", C.xDate, y + C.dateOffsetY, C.dateFont, false, "date");

    y += 4 * lh(C.invoiceFont);
    y += 7;

    // El beneficiario comparte línea con el monto: se limita para que nunca lo pise.
    // Espacio = desde el nombre hasta donde empiezan los dígitos del monto (con sus ajustes).
    const amountText = padL(money(c.amount), 16) + "   ";
    const fo = o.fieldOffsets || {};
    const off = (key) => Number((fo[key] || {}).x) || 0;
    const amountStart = C.xAmount + off("amount") +
      (amountText.length - amountText.trimStart().length) * charWidth(C.amountFont);
    const name = fitText(c.SupplierName, amountStart - C.supplierGap - (C.xSupplier + off("supplier")),
      C.amountFont, C.supplierMinFont);
    push(name.text, C.xSupplier, y + C.ySupplierOffset + name.dy, name.font, true, "supplier");
    push(amountText, C.xAmount, y, C.amountFont, C.amountBold, "amount");

    y += 2 * lh(C.invoiceFont);
    y += 1;

    pushWrapped(push, montoEnLetras(c.amount), C.xWords, y, C.wordsFont, C.wordsWidth, lh(C.wordsFont), C.wordsContIndent, "words");

    return { Elements: elements };
  });
}

// BANCO POPULAR (DOP) -> monto en letras en ESPAÑOL
export function buildPopularCheckPages(cheques, opts = {}) {
  return buildPopularStyleCheckPages(cheques, opts, POPULAR, DEFAULTS.popular);
}

// BANRESERVAS (DOP) -> igual que Popular pero con el bloque inferior 2 líneas más abajo.
export function buildBanreservasCheckPages(cheques, opts = {}) {
  return buildPopularStyleCheckPages(cheques, opts, BANRESERVAS, DEFAULTS.banreservas);
}

// ---------------------------------------------------------------------------
// CODEVI (USD) -> monto en letras en INGLÉS
// ---------------------------------------------------------------------------
export function buildCodeviCheckPages(cheques, opts = {}) {
  const o = { ...DEFAULTS.codevi, ...opts };
  const lh = (pt) => pt * o.lineFactor;
  const C = CODEVI;

  return cheques.map((c) => {
    const elements = [];
    const push = makePusher(elements, o);
    let y = C.startY;

    const invoices = c.InvoiceDetails || [];
    const shown = invoices.slice(0, C.maxInvoices);

    shown.forEach((inv) => {
      // RECIBO No. + FACTURA No. combinados: SOLO el No. de factura (auto-encoge si excede).
      push(inv.VoucherNo, C.xInvoices, y, fitFont(inv.VoucherNo, C.invoiceNoChars, C.invoiceFont, C.cellMinFont), false, "table");
      // FECHA DE FACT.
      push(inv.invoiceDate, C.xFecha, y, C.invoiceFont, false, "table");
      // Montos en bloque, en X fija -> siempre alineados con sus casillas.
      const montos =
        `${padL(money(inv.invoiceAmount), 20)} ${padL(money(inv.amountPaid), 19)}  ` +
        `${padL(money(inv.discountTaken), 15)}  ${padL(money(inv.netCheckAmount), 15)}`;
      push(montos, C.xMoney, y, C.invoiceFont, false, "table");
      y += lh(C.invoiceFont);
    });

    if (invoices.length > C.maxInvoices) {
      const extra = invoices.length - C.maxInvoices;
      push(`VER ANEXO -> ${extra} FACTURA(S) ADICIONAL(ES) EN LISTADO PENDIENTE`, C.xInvoices, y, C.invoiceFont, true, "table");
      y += lh(C.invoiceFont);
    }

    const used = invoices.length > C.maxInvoices ? C.maxInvoices + 1 : invoices.length;
    for (let i = 0; i < C.tableLines - used; i++) y += lh(C.invoiceFont);

    // Encabezado: número de cheque, fecha y suplidor
    const headerText = `${padR(c.chequeNumber, 9)} ${padR(c.chequeDate, 11)} ${padR(c.SupplierId, 10)}     `;
    push(headerText, C.xHeader, y, C.headerFont, false, "header");
    y += 2 * lh(C.headerFont); // el formato original deja una línea en blanco extra

    for (let i = 0; i < C.blankAfterHeader; i++) y += lh(C.invoiceFont);

    push(`US   ${padL(money(c.amount), 12)}   `, C.xAmount, y, C.amountFont, true, "amount");
    y += 1;

    y = pushWrapped(push, amountToWordsEn(c.amount), C.xWords, y, C.wordsFont, C.wordsWidth, lh(C.wordsFont), 0, "words");
    y += C.gapAfterWords;

    // Nombre y dirección limitados al ancho del formulario (no se salen del papel).
    const room = C.maxRight - C.xSupplier - (Number(((o.fieldOffsets || {}).supplier || {}).x) || 0);
    const name = fitText(c.SupplierName, room, C.supplierFont, C.supplierMinFont);
    push(name.text, C.xSupplier, y + name.dy, name.font, false, "supplier");
    y += C.gapSupplierToAddress;
    const address = fitText(c.SupplierAddress, room, C.supplierFont, C.supplierMinFont);
    push(address.text, C.xSupplier, y + address.dy, address.font, false, "supplier");

    return { Elements: elements };
  });
}

// Selector por tipo de banco: "popular" | "banreservas" | "codevi"
export function validateCalibration(bankType, opts = {}) {
  if (!Object.prototype.hasOwnProperty.call(DEFAULTS, bankType)) throw new Error("Plantilla de cheque no válida.");
  const cal = { ...DEFAULTS[bankType], ...opts };
  cal.fieldOffsets = normalizeFieldOffsets(bankType, opts.fieldOffsets);
  for (const key of Object.keys(DEFAULTS[bankType])) {
    if (cal[key] === "" || cal[key] == null || !Number.isFinite(Number(cal[key]))) {
      throw new Error(`Calibración no válida: ${key}.`);
    }
    cal[key] = Number(cal[key]);
  }
  if (Math.abs(cal.dateOffsetX) > 100 || Math.abs(cal.dateOffsetY) > 100) {
    throw new Error("El ajuste de fecha debe estar entre -100 y 100 (1/100 de pulgada).");
  }
  if (cal.lineFactor <= 0 || cal.lineFactor > 3 || cal.pageHeight <= 0 ||
      cal.pageWidth < 0 || !Number.isInteger(cal.pageWidth) || !Number.isInteger(cal.pageHeight)) {
    throw new Error("Revisa el factor de línea y las dimensiones enteras en 1/100 de pulgada.");
  }
  return cal;
}

// Compara el papel que usó el driver (respuesta de /Print/render) con el alto pedido.
// Si difieren, en formulario continuo el 2º cheque en adelante sale corrido
// (típico: el driver ignora 7" y avanza una hoja Carta de 11").
export function paperWarning(result, requestedHeight) {
  const paper = result && result.paper;
  const effective = paper && Number(paper.effectiveHeight);
  const requested = Number(requestedHeight) || 0;
  if (!effective || !requested || Math.abs(effective - requested) <= 3) return null;
  const inches = (v) => (v / 100).toFixed(2).replace(/\.?0+$/, "");
  return `La impresora usó una hoja de ${inches(effective)}" de alto (${paper.paperName || "sin nombre"}) ` +
    `en vez de ${inches(requested)}". Desde el 2º cheque saldrá corrido: crea en Windows el formulario ` +
    `de ${inches(requested)}" y selecciónalo en las preferencias de la impresora.`;
}

export function buildCheckPages(bankType, cheques, opts) {
  opts = validateCalibration(bankType, opts);
  if (!Array.isArray(cheques) || cheques.length < 1 || cheques.length > 50) {
    throw new Error("Selecciona entre 1 y 50 cheques por lote.");
  }
  const numbers = new Set();
  cheques.forEach((c) => {
    const number = String(c.chequeNumber || "");
    if (!number || numbers.has(number)) throw new Error("Hay números de cheque vacíos o repetidos en el lote.");
    numbers.add(number);
    if (!String(c.SupplierName || "").trim() || !Number.isFinite(Number(c.amount)) ||
        Number(c.amount) <= 0 || Number(c.amount) >= 1e12) {
      throw new Error(`Beneficiario o monto no válido en el cheque ${number}.`);
    }
  });
  // El API solo recibe lo que dibuja: se quita la etiqueta de campo.
  return buildTaggedPages(bankType, cheques, opts).map((page) => ({
    Elements: page.Elements.map((el) => {
      const out = { ...el };
      delete out.Field;
      return out;
    }),
  }));
}

function buildTaggedPages(bankType, cheques, opts) {
  if (bankType === "codevi") return buildCodeviCheckPages(cheques, opts);
  if (bankType === "banreservas") return buildBanreservasCheckPages(cheques, opts);
  return buildPopularCheckPages(cheques, opts);
}

export default {
  MAX_INVOICES_PER_CHECK,
  montoEnLetras,
  montoEnLetrasFr,
  amountToWordsEn,
  buildPopularCheckPages,
  buildBanreservasCheckPages,
  buildCodeviCheckPages,
  buildCheckPages,
  normalizeFieldOffsets,
  CHECK_FIELDS,
  MAX_FIELD_OFFSET,
  paperWarning,
  validateCalibration,
  getCalibration,
  saveCalibration,
};
